import React from 'react';
import { Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { NS } from '../../i18n';
import type {
  CampaignContactabilityColumn,
  CampaignListRow,
  CampaignObjective,
  CampaignOutcomeColumn,
  CampaignPaceColumn,
  CampaignProgressColumn,
  CampaignStatus,
} from '../../services/campaignsClient';
import { objectiveById } from '../../domain/objectives';
import { formatCampaignDate, formatCostPerResult } from '../../utils/campaignFormatting';
import { formatCount, formatPercent } from '../../domain/format';
import { ObjectiveChip } from '../ObjectiveChip/ObjectiveChip';
import './CampaignColumns.css';

/*
 * Figures and dates go through `domain/format` with the app's language, read at
 * render: a formatter built at import would keep the language it was built in.
 */

/**
 * Status → AntD Tag preset. Presets rather than hex: the theme paints each one from its semantic
 * slot (`processing` is the info ramp, which in Silia IS the accent ramp).
 */
const STATUS_TAG_COLOR: Record<CampaignStatus, string> = {
  draft: 'default',
  scheduled: 'processing',
  running: 'success',
  paused: 'warning',
  completed: 'default',
};

export const StatusCell: React.FC<{ status: CampaignStatus }> = ({ status }) => {
  const { t } = useTranslation(NS);
  return (
    <Tag color={STATUS_TAG_COLOR[status]} className="campaign-status-tag">
      {t(`status.${status}`)}
    </Tag>
  );
};

/** Two stacked figures in one cell: the headline value and its raw counts. */
export const DualMetric: React.FC<{ top: React.ReactNode; bottom: React.ReactNode }> = ({
  top,
  bottom,
}) => (
  <div className="campaign-metric">
    <span className="campaign-metric__top">{top}</span>
    <span className="campaign-metric__bottom">{bottom}</span>
  </div>
);

/** Name on top; objective chip and audience underneath. */
export const CampaignCell: React.FC<{ campaign: CampaignListRow }> = ({ campaign }) => (
  <div className="campaign-cell">
    <span className="campaign-cell__name">{campaign.name}</span>
    <div className="campaign-cell__meta">
      <ObjectiveChip objective={campaign.objective} />
      {campaign.audienceDescription ? (
        <span className="campaign-cell__audience">{campaign.audienceDescription}</span>
      ) : null}
    </div>
  </div>
);

export const ProgressCell: React.FC<{ progress: CampaignProgressColumn }> = ({ progress }) => {
  const { t, i18n } = useTranslation(NS);
  const fmtInt = (n: number) => formatCount(n, i18n?.language);
  const pct = Math.max(0, Math.min(100, Math.round(progress.percent)));

  return (
    <div className="campaign-progress">
      <span className="campaign-progress__count">
        {fmtInt(progress.dialed)}{' '}
        <span className="campaign-progress__total">/ {fmtInt(progress.eligibleBase)}</span>
      </span>
      <div
        className="campaign-progress__track"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        aria-label={t('list.progressAria', { percent: pct })}
      >
        <div className="campaign-progress__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const ContactabilityCell: React.FC<{ contactability: CampaignContactabilityColumn }> = ({
  contactability,
}) => {
  const { t, i18n } = useTranslation(NS);
  const lang = i18n?.language;
  const fmtInt = (n: number) => formatCount(n, lang);
  const { rightPartyContacts: rpc, totalAttempts: attempts } = contactability;
  // No attempts is "not measured yet", not 0% — a dash.
  const rate = attempts > 0 ? formatPercent(rpc / attempts, lang) : '—';

  return (
    <DualMetric
      top={rate}
      bottom={t('cell.contactability', { rpc: fmtInt(rpc), attempts: fmtInt(attempts) })}
    />
  );
};

export const OutcomeCell: React.FC<{
  outcome: CampaignOutcomeColumn;
  objective: CampaignObjective;
}> = ({ outcome, objective }) => {
  const { t, i18n } = useTranslation(NS);
  const lang = i18n?.language;
  const cost = outcome.conversions > 0 ? formatCostPerResult(outcome.costPerResult, lang) : null;
  // With nothing converted yet the second line names WHAT counts as a result
  // for this objective ("Promise to pay", "Qualified lead"), never a fake $0.00.
  const conversionLabel = objectiveById(objective)?.conversionLabel;

  let bottom: string;
  if (cost) bottom = t('cell.outcomePerResult', { cost });
  else if (conversionLabel) bottom = t(conversionLabel);
  else bottom = t('cell.outcomeNoResults');

  return <DualMetric top={formatCount(outcome.conversions, lang)} bottom={bottom} />;
};

export const PaceCell: React.FC<{ pace: CampaignPaceColumn }> = ({ pace }) => {
  const { t, i18n } = useTranslation(NS);
  const lang = i18n?.language;
  const fmtInt = (n: number) => formatCount(n, lang);

  let secondLine: string;
  if (pace.completedRange) {
    secondLine = t('cell.paceCompletedRange', {
      start: formatCampaignDate(pace.completedRange.start, lang),
      end: formatCampaignDate(pace.completedRange.end, lang),
    });
  } else if (pace.contactsShort !== null) {
    secondLine = t('cell.paceContactsShort', { count: fmtInt(pace.contactsShort) });
  } else if (pace.etaDate !== null) {
    secondLine = t('cell.paceReaches', { date: formatCampaignDate(pace.etaDate, lang) });
  } else {
    secondLine = t('cell.paceNoPace');
  }

  return (
    <DualMetric
      top={t('cell.paceDailyRate', { rate: fmtInt(pace.dailyRate) })}
      bottom={secondLine}
    />
  );
};
